import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import { RdsDialogService } from '@rds/angular-components';
import { SpdgUserService } from '@spdg/core';
import { Subject, Subscription, takeUntil } from 'rxjs';
import { ApiUser } from 'src/app/modules/designer/components/table-view/table-view';
import { ProfileSettingsComponent } from 'src/app/modules/designer/components/profile-settings/profile-settings.component';
import { DesignerSuiteService } from 'src/app/modules/designer/service/designer-suite.service';
import { PermissionService } from 'src/app/modules/ecb-workbench/service/permission.service';
import { ShareComponent } from 'src/app/page/share/share.component';
import { ShareService } from 'src/app/service/share.service';
import { SidePanelService } from 'src/app/service/side-panel.service';
import { TabCloseService } from 'src/app/service/tab-close.service';
import { WebsocketService } from 'src/app/service/websocket.service';

/** A resolved active user ready for display in the Online chip. */
interface ActiveUserView {
    user_id: string;
    initials: string;
    fullName: string;
}

@Component({
    selector: 'app-common-header',
    templateUrl: './common-header.component.html',
    standalone: false,
    styleUrl: './common-header.component.scss',
})
export class CommonHeaderComponent implements OnInit, OnDestroy {
    private readonly gSiteUrl = import.meta.env.NG_APP_CONTENT_GSITE_URL;
    private readonly helpUrl = import.meta.env.NG_APP_CONTENT_HELP_URL;
    private readonly helpUrlEC = import.meta.env.NG_APP_CONTENT_HELP_URL_EC;

    private readonly landingPageUrl = import.meta.env.NG_APP_LANDING_PAGE_URL;
    @Input() avatarInitials = '';
    @Input() isSOA = false;

    /** True when a side panel is open; collapses Share/Back buttons to icons */
    isSidePanelOpen = false;
    private sidePanelSub?: Subscription;

    /** Raw user IDs received from the active-users (presence) socket. */
    private activeUserIds: string[] = [];
    /** Resolved active users (matched against project collaborators). */
    activeUsers: ActiveUserView[] = [];
    /** Max number of avatar badges shown before collapsing into +n. */
    readonly maxVisibleActiveUsers = 3;
    /** Guards against repeated collaborator refetches for the same unknown set. */
    private isRefetchingCollaborators = false;
    private lastUnresolvedSignature = '';

    private readonly destroy$ = new Subject<void>();

    @Input() collaboratorCount: number = 0;
    @Input() elementName: string = '';
    public isAvatarMenuOpen = false;

    @Input() scenarioTitle: string = '';
    @Input() showShareButton: boolean = false;
    @Input() subTitle: string = '';

    @Input() title: string = '';
    @Input() user_role: string = '';

    constructor(
        private readonly tabCloseService: TabCloseService,
        private readonly userService: SpdgUserService,
        private readonly designerService: DesignerSuiteService,
        private readonly permissionService: PermissionService,
        private readonly dialogService: RdsDialogService,
        private readonly sidePanelService: SidePanelService,
        private readonly websocketService: WebsocketService,
        private readonly shareService: ShareService
    ) {}

    ngOnInit(): void {
        // Reset on (re)load so a stale open-state can't carry across views.
        this.sidePanelService.setOpen(false);
        this.sidePanelSub = this.sidePanelService.isOpen$.subscribe((open) => {
            this.isSidePanelOpen = open;
        });

        this.subscribeToActiveUsers();
        this.connectActiveUsers();

        // Metadata may not be available yet at init. Once it arrives (and on
        // later changes, e.g. after sharing with someone new), (re)connect the
        // presence socket and re-resolve names against the latest collaborators.
        this.designerService.eventsProjectMetaData$
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                this.connectActiveUsers();
                this.resolveActiveUsers();
            });
    }

    ngOnDestroy(): void {
        this.sidePanelSub?.unsubscribe();
        this.websocketService.disconnectActiveUsers();
        this.destroy$.next();
        this.destroy$.complete();
    }

    /** Subscribe to presence updates from the active-users service. */
    private subscribeToActiveUsers(): void {
        this.websocketService.activeUsers$
            .pipe(takeUntil(this.destroy$))
            .subscribe((message) => {
                this.activeUserIds = message?.active_users ?? [];
                this.resolveActiveUsers();
            });
    }

    /** Open the presence connection for the current instance and user. */
    private connectActiveUsers(): void {
        const data = this.designerService.getData() as any;
        const instanceId = data?.instance_id;
        const currentUserId = this.getCurrentUserId(data?.collaborators);
        // app_type identifies the module: 'soa' for SOA, 'ec' for ECB.
        const appType = this.isSOA ? 'soa' : 'ec';
        if (instanceId && currentUserId) {
            this.websocketService.connectActiveUsers(
                currentUserId,
                instanceId,
                appType
            );
        }
    }

    private getCurrentUserId(collaborators?: ApiUser[]): string {
        return collaborators?.find((c) => c.current_user)?.user_id ?? '';
    }

    /**
     * Map active user IDs to project collaborators for display.
     *
     * Edge case: the local collaborators list is a snapshot taken when this
     * tab loaded. If a user was added as a collaborator after that snapshot
     * and then comes online, their ID arrives over the socket but can't be
     * resolved here. When that happens we refetch collaborators once and
     * re-resolve, so newly added online users still appear in the chip.
     */
    private resolveActiveUsers(): void {
        const collaborators =
            (this.designerService.getData() as any)?.collaborators ?? [];

        const byId = new Map<string, ApiUser>(
            collaborators.map((c: ApiUser) => [c.user_id, c])
        );

        const resolved: ActiveUserView[] = [];
        const unresolvedIds: string[] = [];

        for (const userId of this.activeUserIds) {
            const match = byId.get(userId);
            if (match) {
                resolved.push({
                    user_id: match.user_id,
                    initials: match.initials,
                    fullName: match.full_name,
                });
            } else {
                unresolvedIds.push(userId);
            }
        }

        this.activeUsers = resolved;

        if (unresolvedIds.length > 0) {
            this.handleUnresolvedActiveUsers(unresolvedIds);
        }
    }

    /**
     * Refetch collaborators once when online user IDs can't be resolved
     * against the local (possibly stale) snapshot, then re-resolve.
     */
    private handleUnresolvedActiveUsers(unresolvedIds: string[]): void {
        const signature = [...unresolvedIds].sort((a, b) => a.localeCompare(b)).join(',');
        // Avoid looping on IDs we already tried and still can't resolve
        // (e.g. a collaborator who was removed but is briefly still online).
        if (
            this.isRefetchingCollaborators ||
            signature === this.lastUnresolvedSignature
        ) {
            return;
        }

        const data = this.designerService.getData() as any;
        const projectId = data?.project_id;
        const projectVersionId = data?.project_version;
        if (!projectId || !projectVersionId) {
            return;
        }

        this.isRefetchingCollaborators = true;
        this.lastUnresolvedSignature = signature;

        this.shareService
            .fetchCollaborators(projectId, projectVersionId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (response) => {
                    this.isRefetchingCollaborators = false;
                    if (response?.is_success && response.data) {
                        const current = this.designerService.getData() as any;
                        if (current) {
                            current.collaborators =
                                response.data.collaborators;
                            current.max_owners = response.data.max_owners;
                            this.designerService.setData(current);
                        }
                        this.resolveActiveUsers();
                    }
                },
                error: () => {
                    this.isRefetchingCollaborators = false;
                },
            });
    }

    /** Users shown as individual avatar badges (capped at maxVisibleActiveUsers). */
    get visibleActiveUsers(): ActiveUserView[] {
        return this.activeUsers.slice(0, this.maxVisibleActiveUsers);
    }

    /** Number of users beyond the visible badges, shown as +n. */
    get hiddenActiveUsersCount(): number {
        return Math.max(0, this.activeUsers.length - this.maxVisibleActiveUsers);
    }

    /** Full names hidden behind the +n badge, comma separated. */
    get hiddenActiveUsersLabel(): string {
        return this.activeUsers
            .slice(this.maxVisibleActiveUsers)
            .map((u) => u.fullName)
            .join(', ');
    }

    logout(): void {
        this.isAvatarMenuOpen = false;
        this.userService.logout();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        const isAvatarContainer = target.closest('.avatar-menu-container');
        if (!isAvatarContainer) {
            this.isAvatarMenuOpen = false;
        }
    }

    openGSite(): void {
        globalThis.open(this.gSiteUrl, '_blank');
        this.isAvatarMenuOpen = false;
    }

    openHelp(): void {
        const url = this.isSOA ? this.helpUrl : this.helpUrlEC;
        globalThis.open(url, '_blank');
        this.isAvatarMenuOpen = false;
    }

    openProfileSettings(event?: MouseEvent): void {
        event?.stopPropagation();
        this.isAvatarMenuOpen = false;
        this.dialogService.open(ProfileSettingsComponent, {
            size: 'm',
            maxHeight: '80vh',
        });
    }

    openShareDialog(): void {
        const projectDetails = this.designerService.getData() as any;
        this.dialogService
            .open(ShareComponent, {
                data: {
                    project_id: projectDetails.project_id,
                    project_version_id: projectDetails.project_version,
                    project_name: projectDetails.project_name,
                    updated_on: projectDetails.last_refresh_timestamp,
                    user_role: projectDetails.user_role,
                    copyLinkUrl: globalThis.location.href,
                    activeUserIds: this.activeUsers.map((u) => u.user_id),
                    max_owners: projectDetails.max_owners,
                },
                size: 'l',
            })
            .afterClosed()
            .subscribe((result: any) => {
                if (!result) return;

                const currentData = this.designerService.getData() as any;
                if (!currentData) return;

                let dataChanged = false;

                if (
                    result.user_role &&
                    currentData.user_role !== result.user_role
                ) {
                    currentData.user_role = result.user_role;
                    dataChanged = true;

                    const rolePermissionMap =
                        this.permissionService.getRolePermissionMap();
                    if (rolePermissionMap) {
                        this.permissionService.setPermissions(
                            rolePermissionMap,
                            result.user_role
                        );
                    }
                }

                if (result.collaborators) {
                    currentData.collaborators = result.collaborators;
                    this.collaboratorCount = result.collaborators.length;
                    dataChanged = true;
                }

                if (result.max_owners) {
                    currentData.max_owners = result.max_owners;
                    dataChanged = true;
                }

                if (dataChanged) {
                    this.designerService.setData(currentData);
                    this.designerService.emitProjectMetaData(currentData);
                }
            });
    }

    redirectToLandingPage() {
        globalThis.location.href = this.landingPageUrl + '/app/project';
    }

    redirectToScenario() {
        this.tabCloseService.closeTab();
    }

    toggleAvatarMenu(): void {
        this.isAvatarMenuOpen = !this.isAvatarMenuOpen;
    }
}
